// Turns the credit-rating record into something a salesperson can read.
//
// The stored record is deliberately raw: French slugs, counters, nulls. That is
// the right shape for a database and the wrong shape for a screen. Everything
// user-facing is built here, once, so the contact view, the edit form and the
// contact list all say the same thing in the same words.

// What the rating means, in one line. Derived from the rating, never from the
// stored French text, so the wording stays in English whatever produced the row.
const TERMS = {
  5: 'Suggested terms: 30 days net',
  4: 'Suggested terms: no deposit, balance within 8 days of delivery',
  3: 'Suggested terms: 30% deposit, balance within 8 days of delivery',
  2: 'Suggested terms: payment in full before production',
  1: 'Suggested terms: payment in full before production'
}

// SIGNALS — the things that actually moved the rating. Worth a chip.
const SIGNALS = {
  blocage: { label: 'On hold', color: 'danger' },
  client_fidele: { label: 'Long-standing, never chased', color: 'success' },
  jamais_relance: { label: 'Never chased for payment', color: 'success' },
  retard_grave: { label: 'Paid over 60 days late', color: 'danger' },
  relance_finale: { label: 'Final reminder sent', color: 'danger' },
  retard_chronique: { label: 'Repeatedly late', color: 'warning' },
  retard_30_60: { label: 'Paid 30-60 days late', color: 'warning' },
  retard_leger: { label: 'Slightly overdue', color: 'warning' },
  acompte_impaye: { label: 'Deposit unpaid', color: 'warning' },
  litige: { label: 'Invoice disputed', color: 'primary' },
  primo_affaire: { label: 'First order', color: 'primary' },
  delai_public: { label: 'Public body', color: 'primary' },
  cote_manuelle: { label: 'Set by hand', color: 'primary' }
}

// CAVEATS — what we could NOT see. True on almost every contact, so showing
// them as chips would put the same two badges on 2 600 screens and teach
// everyone to ignore the badges. They become one quiet sentence instead.
const CAVEATS = {
  jamais_a_credit: 'this customer has never been given payment terms, so the record shows they pay, not that they pay on credit',
  donnees_insuffisantes: 'no financial statements on file',
  historique_partiel: 'payment dates missing on part of the history',
  echeance_non_definie: 'no payment terms set on this contact'
}

const SOURCES = {
  interne: 'payment history',
  externe: 'financial analysis',
  manuel: 'a manual decision'
}

const MOIS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function jolieDate (iso) {
  if (!iso) return null
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return String(iso).slice(0, 10)
  return `${Number(m[3])} ${MOIS[Number(m[2]) - 1]} ${m[1]}`
}

const n = (v) => (v == null ? 0 : Number(v))

export default {
  computed: {
    // The rating itself. `null` is NOT a bad score, it means nobody looked —
    // so it never borrows the look of a good one.
    creditLabel () {
      const r = this.creditRating
      if (!r || r.cote_appliquee === null || r.cote_appliquee === undefined) return 'Not rated'
      return `${r.cote_appliquee}/5`
    },
    creditColor () {
      const c = this.creditRating ? this.creditRating.cote_appliquee : null
      if (c === null || c === undefined) return 'dark'
      if (c >= 4) return 'success'
      if (c === 3) return 'warning'
      return 'danger'
    },
    creditTerms () {
      const c = this.creditRating ? this.creditRating.cote_appliquee : null
      return TERMS[c] || null
    },
    creditSignals () {
      const f = (this.creditRating && this.creditRating.drapeaux) || []
      return f.filter(x => SIGNALS[x]).map(x => ({ key: x, ...SIGNALS[x] }))
    },
    // What we could NOT see. Kept short, and kept from repeating the summary:
    // when no settled invoice carries a date at all, the summary has already
    // said so in plainer words, so the caveat stays quiet about it.
    creditCaveat () {
      const r = this.creditRating
      const f = (r && r.drapeaux) || []
      const e = (r && r.evidence) || {}
      const parts = []
      if (f.includes('donnees_insuffisantes')) {
        parts.push('No financial statements on file, so this looks only at how the customer pays us.')
      }
      if (f.includes('historique_partiel') && n(e.n_soldees_datees) > 0) {
        parts.push('Payment dates are missing on part of the history.')
      }
      if (f.includes('echeance_non_definie')) {
        parts.push('No payment terms are set on this contact, so lateness cannot be measured.')
      }
      // A spotless record built on cash purchases is not the same as a spotless
      // record on credit. The number stays, the caveat says what it rests on.
      if (f.includes('jamais_a_credit')) {
        parts.push('This customer has never been given payment terms, so the record shows that they pay, not that they pay on credit.')
      }
      return parts.length ? parts.join(' ') : null
    },
    creditProvenance () {
      const r = this.creditRating
      if (!r) return null
      const d = jolieDate(r.date_analyse)
      return `Checked ${d} from ${SOURCES[r.source] || r.source}.`
    },

    // The headline sentence. This is what replaces the old dump of slugs: it
    // says, in plain words, why the contact reads the way it does.
    creditSummary () {
      const r = this.creditRating
      if (!r) return 'No credit check has been run on this contact yet.'
      const e = r.evidence || {}
      if (r.cote_appliquee === null || r.cote_appliquee === undefined) {
        if (!n(e.n_factures)) return 'Nothing invoiced yet, so there is nothing to rate.'
        if (n(e.n_soldees) && !n(e.n_soldees_datees)) {
          return `${n(e.n_soldees)} invoice${n(e.n_soldees) > 1 ? 's' : ''} settled, none matched to a bank transfer. We know they were paid, not whether they were paid on time.`
        }
        return 'Not enough payment history to rate this contact.'
      }
      if (n(e.n_impayees)) {
        const worst = e.pire_retard_courant
        if (worst != null && Number(worst) > 0) {
          return `${n(e.n_impayees)} unpaid invoice${n(e.n_impayees) > 1 ? 's' : ''}, the oldest ${Number(worst)} days past due.`
        }
        return `${n(e.n_impayees)} unpaid invoice${n(e.n_impayees) > 1 ? 's' : ''}, none past due yet.`
      }
      // A customer who has never been chased earned that, and the screen should
      // say so rather than shrug with a generic line.
      const f = r.drapeaux || []
      if (f.includes('client_fidele') || f.includes('jamais_relance')) {
        return `${n(e.n_soldees_suivies)} invoices settled since 2022, not one payment reminder sent.`
      }
      return 'Nothing outstanding today.'
    },

    // The detail rows. Only what says something: a table of zeros teaches the
    // reader to stop looking at the table.
    creditDetails () {
      const r = this.creditRating
      const e = (r && r.evidence) || null
      if (!e) return []
      const rows = []
      const money = (v) => `${Math.round(Number(v)).toLocaleString('en-GB')} EUR`

      if (n(e.n_factures)) {
        rows.push({ k: 'Invoices', v: `${n(e.n_factures)} issued, ${n(e.n_soldees)} settled, ${n(e.n_impayees)} open` })
      }
      if (n(e.encours_impaye) > 0) rows.push({ k: 'Outstanding', v: money(e.encours_impaye) })
      if (e.pire_retard_courant != null && Number(e.pire_retard_courant) > 0) {
        rows.push({ k: 'Oldest overdue', v: `${Number(e.pire_retard_courant)} days` })
      }
      if (n(e.n_soldees)) {
        const d = n(e.n_soldees_datees)
        rows.push({
          k: 'Payment dates known',
          v: d
            ? `${d} of ${n(e.n_soldees)} settled invoices`
            : `none of ${n(e.n_soldees)} settled invoices — tick the bank transfer when marking an invoice paid and this fills in`
        })
      }
      if (n(e.n_soldees_datees)) {
        rows.push({
          k: 'Paid on time',
          v: `${n(e.n_ontime)} on time, ${n(e.n_late_1_30)} up to 30 days late, ${n(e.n_late_31_60) + n(e.n_late_60plus) || n(e.n_late_30plus)} over 30 days late`
        })
      }
      if (n(e.n_soldees_suivies)) {
        rows.push({
          k: 'Payment reminders',
          v: n(e.n_relancees)
            ? `${n(e.n_relancees)} invoice(s) had to be chased since 2022`
            : `never chased, across ${n(e.n_soldees_suivies)} invoices settled since 2022`
        })
      }
      rows.push({
        k: 'Payment terms',
        v: Number(e.payment_due_day) > 0
          ? `${Number(e.payment_due_day)} days`
          : 'none on file — invoices fall due on the day they are issued'
      })
      if (n(e.n_litiges)) rows.push({ k: 'Disputed', v: `${n(e.n_litiges)} invoice(s)` })
      return rows
    }
  },
  methods: {
    // Used by the contact list, which receives the flags as a plain string.
    creditFlagsToEnglish (raw) {
      if (!raw) return ''
      return String(raw).split(',').map(x => x.trim())
        .filter(x => SIGNALS[x])
        .map(x => SIGNALS[x].label)
        .join(', ')
    }
  }
}

export { SIGNALS, CAVEATS, TERMS }
